/**
 * Explicit Pay-now step for yearly mid-cycle pack upgrades.
 * Saved card (token) pre-selected, or enter a new card.
 */
import { useEffect, useMemo, useState } from 'react';
import Cards, { type Focused } from 'react-credit-cards-2';
import 'react-credit-cards-2/dist/es/styles-compiled.css';
import {
  INBOUND_FREE_MONTHLY,
  INBOUND_PACK_VOLUME,
  SEAT_PACK_INCLUDED_INBOUND,
  monthsUntilYmd,
  packPricing,
  resolveSeatCount,
  yearlyMidCycleUpgradeCharge,
} from '@x9/billing';
import PortalLoader from '../components/PortalLoader';
import { invalidateBillingSummaryCache } from '../hooks/useBillingSummary';
import {
  callableErrorMessage,
  confirmPackUpgradeCheckout,
  fetchBillingSubscription,
} from '../services/api';

type Props = {
  seatCount: number;
  volumePacks: number;
  onBack: () => void;
  onDone: () => void;
};

function brl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

function formatCardNumber(value: string): string {
  return digitsOnly(value)
    .slice(0, 19)
    .replace(/(\d{4})(?=\d)/g, '$1 ')
    .trim();
}

function formatExpiryInput(value: string): string {
  const d = digitsOnly(value).slice(0, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}

function parseExpiry(value: string): { month: string; year: string } | null {
  const cleaned = value.replace(/\s/g, '');
  const m = cleaned.match(/^(\d{2})\s*[\/]?\s*(\d{2}|\d{4})$/);
  if (!m) return null;
  const month = m[1];
  let year = m[2];
  if (year.length === 2) year = `20${year}`;
  const mi = Number(month);
  if (mi < 1 || mi > 12) return null;
  return { month: month.padStart(2, '0'), year };
}

type CardMode = 'saved' | 'new';

export default function PackUpgradeCheckoutPage({
  seatCount,
  volumePacks,
  onBack,
  onDone,
}: Props) {
  const [ready, setReady] = useState(false);
  const [savedCardHint, setSavedCardHint] = useState<string | null>(null);
  const [hasSavedCard, setHasSavedCard] = useState(false);
  const [profile, setProfile] = useState<{
    legalName: string | null;
    email: string | null;
    cpfCnpj: string | null;
    phone: string | null;
    postalCode: string | null;
    addressNumber: string | null;
    renewalDueDate: string | null;
    currentSeatCount: number;
    currentVolumePacks: number;
  } | null>(null);

  const [cardMode, setCardMode] = useState<CardMode>('saved');
  const [cardHolderName, setCardHolderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCcv, setCardCcv] = useState('');
  const [cardFocus, setCardFocus] = useState<Focused>('');
  const [postalCode, setPostalCode] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchBillingSubscription();
        if (cancelled) return;
        const p = data.billingProfile;
        const active = (data.subscriptions || []).find((s) => {
          const st = String((s as { status?: string }).status || '');
          return ['ACTIVE', 'PAST_DUE', 'GRACE_PERIOD', 'CANCEL_AT_PERIOD_END'].includes(st);
        }) as {
          seatCount?: number;
          seatPacks?: number;
          volumePacks?: number;
          renewalDueDate?: string;
          nextDueDate?: string;
        } | undefined;

        const hint = p?.cardLast4
          ? (p.cardBrand ? `${p.cardBrand} •••• ${p.cardLast4}` : `Cartão •••• ${p.cardLast4}`)
          : null;
        setSavedCardHint(hint);
        setHasSavedCard(!!hint);
        setCardMode(hint ? 'saved' : 'new');
        if (p?.legalName) setCardHolderName(p.legalName);
        if (p?.postalCode) setPostalCode(p.postalCode);
        if (p?.addressNumber) setAddressNumber(p.addressNumber);

        setProfile({
          legalName: p?.legalName || null,
          email: p?.email || null,
          cpfCnpj: p?.cpfCnpj || null,
          phone: p?.phone || null,
          postalCode: p?.postalCode || null,
          addressNumber: p?.addressNumber || null,
          renewalDueDate: active?.renewalDueDate || active?.nextDueDate || null,
          currentSeatCount: resolveSeatCount(active),
          currentVolumePacks: Number.isFinite(Number(active?.volumePacks))
            ? Number(active?.volumePacks)
            : 0,
        });
      } catch {
        /* non-blocking */
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pricing = useMemo(
    () => packPricing({ seatCount, volumePacks, interval: 'YEARLY' }),
    [seatCount, volumePacks],
  );

  const stub = useMemo(() => {
    if (!profile) return null;
    const currentMonthly = packPricing({
      seatCount: profile.currentSeatCount,
      volumePacks: profile.currentVolumePacks,
      interval: 'MONTHLY',
    }).monthlyCents;
    return yearlyMidCycleUpgradeCharge({
      currentMonthlyCents: currentMonthly,
      targetMonthlyCents: pricing.monthlyCents,
      openMonths: monthsUntilYmd(profile.renewalDueDate),
    });
  }, [profile, pricing]);

  const inboundTotal =
    INBOUND_FREE_MONTHLY
    + (seatCount > 0 ? SEAT_PACK_INCLUDED_INBOUND : 0)
    + volumePacks * INBOUND_PACK_VOLUME;

  const cardDigits = digitsOnly(cardNumber);
  const expiry = parseExpiry(cardExpiry);
  const newCardReady =
    !!cardHolderName.trim()
    && cardDigits.length >= 13
    && !!expiry
    && digitsOnly(cardCcv).length >= 3
    && digitsOnly(postalCode).length === 8
    && !!addressNumber.trim()
    && !!profile?.cpfCnpj
    && !!profile?.email;

  const canPay =
    accepted
    && !!stub
    && stub.chargeNowCents > 0
    && (cardMode === 'saved' ? hasSavedCard : newCardReady);

  const pay = async () => {
    setError(null);
    if (!canPay || !stub) return;
    setBusy(true);
    try {
      const res = await confirmPackUpgradeCheckout({
        seatCount,
        volumePacks,
        useSavedCard: cardMode === 'saved',
        ...(cardMode === 'new' && expiry
          ? {
              creditCard: {
                holderName: cardHolderName.trim(),
                number: cardDigits,
                expiryMonth: expiry.month,
                expiryYear: expiry.year,
                ccv: digitsOnly(cardCcv),
              },
              creditCardHolderInfo: {
                name: cardHolderName.trim(),
                email: String(profile?.email || '').trim(),
                cpfCnpj: digitsOnly(String(profile?.cpfCnpj || '')),
                postalCode: digitsOnly(postalCode),
                addressNumber: addressNumber.trim(),
                phone: profile?.phone ? digitsOnly(profile.phone) : undefined,
                mobilePhone: profile?.phone ? digitsOnly(profile.phone) : undefined,
              },
            }
          : {}),
      });
      if (!res.ok) {
        setError('Não foi possível concluir o pagamento. Tente de novo.');
        return;
      }
      invalidateBillingSummaryCache();
      onDone();
    } catch (e) {
      setError(callableErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="my-shell max-w-lg">
      <button type="button" className="mb-4 text-sm text-muted-foreground underline" onClick={onBack}>
        ← Voltar
      </button>

      <h1 className="text-2xl font-bold text-foreground">Confirmar pagamento</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Extensão do plano Equipe até a próxima renovação anual.
      </p>

      {!ready && <PortalLoader />}

      {ready && stub && (
        <div className="mt-6 space-y-4">
          <div className="my-card space-y-2">
            <p className="text-sm text-foreground">
              Pagar agora:{' '}
              <span className="text-xl font-bold">{brl(stub.chargeNowCents)}</span>
            </p>
            <p className="text-xs text-muted-foreground">
              {stub.openMonths} mês(es) restantes × diferença mensal, com −20% anual
            </p>
            <p className="text-sm text-foreground">
              Renovação anual:{' '}
              <span className="font-bold">{brl(pricing.chargeCents)}</span>/ano
            </p>
            <p className="text-xs text-muted-foreground">
              {seatCount} assentos · {inboundTotal} contribuições/mês
              {profile?.renewalDueDate ? ` · renovação em ${profile.renewalDueDate}` : ''}
            </p>
          </div>

          <div className="my-card space-y-3">
            <p className="text-sm font-semibold text-foreground">Forma de pagamento</p>

            {hasSavedCard && savedCardHint && (
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3">
                <input
                  type="radio"
                  name="cardMode"
                  checked={cardMode === 'saved'}
                  onChange={() => setCardMode('saved')}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-foreground">Cartão cadastrado</span>
                  <span className="text-xs text-muted-foreground">{savedCardHint}</span>
                </span>
              </label>
            )}

            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3">
              <input
                type="radio"
                name="cardMode"
                checked={cardMode === 'new'}
                onChange={() => setCardMode('new')}
                className="mt-1"
              />
              <span className="text-sm font-medium text-foreground">
                {hasSavedCard ? 'Usar outro cartão' : 'Cartão de crédito'}
              </span>
            </label>

            {cardMode === 'new' && (
              <div className="space-y-3 pt-1">
                <div className="my-card-preview">
                  <Cards
                    number={cardNumber}
                    name={cardHolderName}
                    expiry={cardExpiry}
                    cvc={cardCcv}
                    focused={cardFocus}
                  />
                </div>
                <label className="my-label">
                  Nome no cartão
                  <input
                    className="my-input"
                    value={cardHolderName}
                    onChange={(e) => setCardHolderName(e.target.value)}
                    onFocus={() => setCardFocus('name')}
                  />
                </label>
                <label className="my-label">
                  Número
                  <input
                    className="my-input"
                    inputMode="numeric"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    onFocus={() => setCardFocus('number')}
                  />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="my-label">
                    Validade
                    <input
                      className="my-input"
                      placeholder="MM/AA"
                      value={cardExpiry}
                      onChange={(e) => setCardExpiry(formatExpiryInput(e.target.value))}
                      onFocus={() => setCardFocus('expiry')}
                    />
                  </label>
                  <label className="my-label">
                    CVV
                    <input
                      className="my-input"
                      inputMode="numeric"
                      value={cardCcv}
                      onChange={(e) => setCardCcv(digitsOnly(e.target.value).slice(0, 4))}
                      onFocus={() => setCardFocus('cvc')}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="my-label">
                    CEP
                    <input
                      className="my-input"
                      inputMode="numeric"
                      value={postalCode}
                      onChange={(e) => setPostalCode(digitsOnly(e.target.value).slice(0, 8))}
                    />
                  </label>
                  <label className="my-label">
                    Número
                    <input
                      className="my-input"
                      value={addressNumber}
                      onChange={(e) => setAddressNumber(e.target.value)}
                    />
                  </label>
                </div>
              </div>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              className="mt-1"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span>
              Autorizo a cobrança de {stub ? brl(stub.chargeNowCents) : '—'} agora e a renovação
              anual no novo valor ({brl(pricing.chargeCents)}/ano).
            </span>
          </label>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="button"
            className="my-btn-primary"
            disabled={busy || !canPay}
            onClick={() => void pay()}
          >
            {busy ? 'Processando…' : `Pagar agora ${brl(stub.chargeNowCents)}`}
          </button>
        </div>
      )}

      {ready && (!stub || stub.chargeNowCents <= 0) && (
        <p className="mt-6 text-sm text-muted-foreground">
          Não há cobrança imediata para esta alteração. Volte e confirme na Conta.
        </p>
      )}
    </div>
  );
}
